import { useMemo, ReactNode } from 'react';
import { Plus, ShoppingCart, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Spinner } from '@/components/ui/spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';

import { formatPriceDisplay } from '../../../util/formatting';
import { ROUTES, buildRoute } from '../../../config/routes';

import { useCart } from './hooks/useCart';
import { useCartPageActions } from './hooks/useCartPageActions';
import { groupAssetsByProperty } from './hooks/useCartGrouping';
import { useFreeFloorPlanImage } from './floor-plan/useFreeFloorPlanOffer';
import type { PropertyCartItem } from './types/cart.types';

import CartSection from './components/CartSection';
import AccountPropertyCartTable from './components/AccountPropertyCartTable';
import AssetCartTable from './components/AssetCartTable';
import CartDialogs from './components/CartDialogs';

const CHECKOUT_DISABLED_REASON =
  'Add services to at least one property to checkout.';

const CartPage = () => {
  const {
    cartId,
    propertyItems,
    assetItems,
    totalPrice,
    totalInitialPrice,
    totalItemCount,
    totalSavingPrice,
    propertyItemsByEntity,
    hasMultipleEntities,
    isInitialLoading,
    mutatingItemIds,
    removePropertyItem,
    removeAssetItem,
    clearCart,
    updatePropertyItem,
    updateAssetGroup,
  } = useCart();

  // Shares the floor plan offer's package-list read: no request of its own.
  const freeFloorPlanImage = useFreeFloorPlanImage({
    skip: propertyItems.length === 0,
  });

  const actions = useCartPageActions({
    removePropertyItem,
    removeAssetItem,
    clearCart,
    updatePropertyItem,
    propertyItems,
    assetItems,
    cartId,
    totalInitialPrice,
    totalSavingPrice,
    totalPrice,
    freeFloorPlanImage,
  });

  const hasPropertyItems = propertyItems.length > 0;
  const hasAssetItems = assetItems.length > 0;
  const hasBothTypes = hasPropertyItems && hasAssetItems;
  const hasAnyItems = hasPropertyItems || hasAssetItems;
  // Without several accounts in the cart, every property line is in this one.
  const singleAccount = propertyItems.find((item) => item.entityUUID)?.entityUUID;

  const groupedAssets = useMemo(
    () => groupAssetsByProperty(assetItems),
    [assetItems],
  );

  // Each account's lines ask about that account's free floor plan (DV-3697).
  const renderPropertyTable = (
    items: PropertyCartItem[],
    entityUUID: string | undefined,
  ): ReactNode => (
    <AccountPropertyCartTable
      entityUUID={entityUUID}
      data={items}
      onEditService={actions.handleEditService}
      onEditAccess={actions.handleEditAccess}
      onDelete={actions.handleDeleteProperty}
      mutatingItemIds={mutatingItemIds}
    />
  );

  const renderEntityPropertySections = (expandable: boolean): ReactNode =>
    propertyItemsByEntity.map((entity) => (
      <CartSection
        key={entity.entityUUID}
        title={entity.entityName}
        orderCount={entity.items.length}
        subtotal={entity.subtotal}
        expandable={expandable}
        defaultExpanded
      >
        {renderPropertyTable(entity.items, entity.entityUUID || undefined)}
      </CartSection>
    ));

  const renderAssetSections = (): ReactNode =>
    groupedAssets.map((group) => (
      <CartSection
        key={group.propertyUUID}
        title={group.propertyAddress}
        subtitle={group.entityName}
        orderCount={group.items.length}
        subtotal={group.subtotal}
        expandable={groupedAssets.length > 1}
        defaultExpanded
        rightAction={
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              actions.navigateTo(
                buildRoute.propertyAssets(group.propertyUUID, {
                  photosetUUID: group.photosetUUID,
                }),
                { source: 'cart' },
              )
            }
          >
            <Plus className="h-4 w-4" />
            Add Services
          </Button>
        }
      >
        <AssetCartTable
          data={group.items}
          onEdit={actions.handleEditAsset}
          onDelete={actions.handleDeleteAsset}
          mutatingItemIds={mutatingItemIds}
        />
      </CartSection>
    ));

  return (
    <>
      <div className="flex h-full w-full flex-col bg-background">
        {/* Header */}
        <div className="shrink-0 border-b border-border px-5 py-4">
          <Typography as="h1" variant="heading-2xl">
            Order Cart
          </Typography>
          <Typography
            as="p"
            variant="body"
            className="text-muted-foreground"
          >
            Place your order for the selected properties
          </Typography>
        </div>

        {/* Action bar */}
        {hasAnyItems && (
          <div className="shrink-0 flex items-center border-b border-border px-5 py-3">
            <Typography variant="body-semibold">
              {totalItemCount} {totalItemCount === 1 ? 'Item' : 'Items'}
            </Typography>
            <Button
              variant="link"
              // Standalone link-styled action (its own row, not inline prose),
              // so it carries the 44px touch floor itself — see button.tsx's
              // link-variant comment.
              className="ml-auto px-0 text-foreground hover:text-primary max-lg:min-h-11"
              onClick={actions.handleClearCart}
            >
              <Trash2 className="h-4 w-4" />
              Clear Cart
            </Button>
          </div>
        )}

        {/* Scrollable content */}
        <div
          className="flex-1 overflow-y-auto"
          aria-busy={isInitialLoading}
        >
          {isInitialLoading && (
            <div
              className="flex h-full w-full items-center justify-center gap-3"
              aria-live="polite"
            >
              <Spinner size="lg" />
              <span className="sr-only">Loading your cart</span>
            </div>
          )}

          {!isInitialLoading && !hasAnyItems && (
            <EmptyState
              icon={ShoppingCart}
              title="Your Cart Is Empty"
              description="Add properties from My properties to start an order."
              action={
                <Button onClick={() => actions.navigateTo(ROUTES.PROPERTIES)}>
                  Browse Properties
                </Button>
              }
            />
          )}

          {!isInitialLoading && hasAnyItems && (
            <section className="space-y-4 p-6">
              {hasBothTypes ? (
                <>
                  <CartSection
                    title="On-site Services"
                    variant="outer"
                    defaultExpanded
                  >
                    <div className="space-y-3 p-3">
                      {hasMultipleEntities
                        ? renderEntityPropertySections(
                            propertyItemsByEntity.length > 1,
                          )
                        : renderPropertyTable(propertyItems, singleAccount)}
                    </div>
                  </CartSection>
                  <CartSection
                    title="Image Edit and Upgrades"
                    variant="outer"
                    defaultExpanded
                  >
                    <div className="space-y-3 p-3">{renderAssetSections()}</div>
                  </CartSection>
                </>
              ) : (
                <>
                  {hasPropertyItems &&
                    (hasMultipleEntities
                      ? renderEntityPropertySections(true)
                      : renderPropertyTable(propertyItems, singleAccount))}
                  {hasAssetItems && renderAssetSections()}
                </>
              )}
            </section>
          )}
        </div>

        {/* Footer — only rendered when the cart has items; the empty state's
            "Browse Properties" CTA is the whole call to action there, and a
            $0 total with a disabled Checkout is noise (QA feedback,
            2026-08-17). */}
        {!isInitialLoading && hasAnyItems && (
          <div className="shrink-0 border-t border-border bg-background px-5 py-3.5">
            {actions.isCheckoutDisabled && (
              <Typography
                as="p"
                variant="body"
                // Tooltips never open on touch, so below lg the disabled-Checkout
                // reason is shown as visible text; desktop keeps the tooltip.
                className="pb-2 text-right text-muted-foreground lg:hidden"
              >
                {CHECKOUT_DISABLED_REASON}
              </Typography>
            )}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                className="min-w-[100px]"
                onClick={() => actions.navigateTo(-1)}
              >
                Back
              </Button>
              <div className="flex items-center gap-5">
                <div className="flex items-baseline gap-1.5">
                  <Typography
                    variant="body-medium"
                    className="text-muted-foreground"
                  >
                    Total
                  </Typography>
                  <Typography variant="heading-md">
                    ${formatPriceDisplay(totalPrice)}
                  </Typography>
                </div>
                {actions.isCheckoutDisabled ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {/* Wrap the disabled button so the tooltip can still receive hover events. */}
                        <span tabIndex={0}>
                          <Button
                            className="min-w-[100px]"
                            disabled
                            aria-disabled
                          >
                            Checkout
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{CHECKOUT_DISABLED_REASON}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <Button
                    className="min-w-[100px]"
                    onClick={actions.handleCheckout}
                  >
                    Checkout
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <CartDialogs
        sliderState={actions.sliderState}
        onSliderClose={actions.handleSliderClose}
        onSliderContinueToAccess={actions.handleSliderContinueToAccess}
        onSliderDone={actions.handleSliderDone}
        onSliderBackToService={actions.handleSliderBackToService}
        assetEditModal={actions.assetEditModal}
        onAssetEditClose={actions.handleAssetEditClose}
        updateAssetGroup={updateAssetGroup}
        removeAssetItem={removeAssetItem}
        deleteConfirmation={actions.deleteConfirmation}
        onConfirmDelete={actions.handleConfirmDelete}
        onCancelDelete={actions.handleCancelDelete}
        showPartialOrderModal={actions.showPartialOrderModal}
        onPartialOrderChange={(open) =>
          !open && actions.handlePartialOrderClose()
        }
        emptyServiceCount={actions.emptyServiceCount}
        onPartialOrderContinue={actions.handlePartialOrderContinue}
      />
    </>
  );
};

export default CartPage;
